/**
 * Custom hook for managing anonymous authentication flow
 * Provides utilities for anonymous login, cart management, and auth state transitions
 */

import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { auth, firestore } from '@tabletuck/firebase-services';
import {
  signInAnonymously,
  createAnonymousUserDoc,
  addAnonymousUserAddress,
  migrateAnonymousUserData,
  getUserAnonymousStatus,
} from '@tabletuck/firebase-services';
import {
  loggedInSuccess,
  setUser,
  setAnonymousStatus,
  setCart,
} from '@tabletuck/redux-slices';

export const useAnonymousAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { token, isLoggedIn, isAnonymous, userData } = useSelector(
    (state) => state.authReducer
  );
  const { cart } = useSelector((state) => state.shopReducer);

  // Check if auth is available
  const authAvailable = !!auth;

  /**
   * Initialize anonymous user session
   * Called when user first interacts with the app (adds item to cart)
   */
  const initializeAnonymousSession = useCallback(
    async (pickupOrDelivery = 'pickup') => {
      try {
        // Check if auth is available
        if (!authAvailable) {
          console.warn(
            '⚠️ [ANONYMOUS] Auth service not available, cannot initialize session'
          );
          return { success: false, error: 'Auth service not initialized' };
        }

        // Check if user is already authenticated
        if (isLoggedIn && !isAnonymous) {
          return { success: true, alreadyAuthenticated: true };
        }

        // Check if already anonymous
        if (isLoggedIn && isAnonymous) {
          return { success: true, alreadyAnonymous: true };
        }

        // Sign in anonymously
        const signInResult = await signInAnonymously(pickupOrDelivery);
        if (!signInResult.success) {
          throw new Error(signInResult.error);
        }

        // Create user document
        const userDocResult = await createAnonymousUserDoc(
          signInResult.user.uid,
          pickupOrDelivery
        );
        if (!userDocResult.success) {
          throw new Error(userDocResult.error);
        }

        // Ensure userData has the uid field for proper Redux state
        const completeUserData = {
          ...userDocResult.userData,
          uid: signInResult.user.uid,
        };

        // Update Redux state
        dispatch(
          loggedInSuccess({
            token: signInResult.user.uid,
            isAnonymous: true,
          })
        );
        dispatch(
          setUser({
            userData: completeUserData,
          })
        );
        dispatch(setAnonymousStatus(true));

        // Validate that everything was set up correctly
        if (!completeUserData.uid) {
          throw new Error('User data missing uid after initialization');
        }

        if (!signInResult.user.uid) {
          throw new Error('User object missing uid after sign-in');
        }

        return {
          success: true,
          user: signInResult.user,
          userData: completeUserData,
        };
      } catch (error) {
        console.error(
          '❌ [ANONYMOUS] Failed to initialize anonymous session:',
          error
        );
        return { success: false, error: error.message };
      }
    },
    [dispatch, isLoggedIn, isAnonymous, authAvailable]
  );

  /**
   * Add item to cart (with anonymous session initialization if needed)
   */
  const addToCartWithAnonymousCheck = useCallback(
    async (item, pickupOrDelivery = 'pickup') => {
      try {
        // Initialize anonymous session if needed
        if (!isLoggedIn) {
          const initResult = await initializeAnonymousSession(pickupOrDelivery);
          if (!initResult.success) {
            throw new Error('Failed to initialize anonymous session');
          }

          // After initialization, we should have a user document
          // Return success so the caller can proceed with their cart logic
          return { success: true, sessionInitialized: true };
        }

        return { success: true, sessionInitialized: false };
      } catch (error) {
        console.error(
          '❌ [ANONYMOUS] Failed to prepare for cart addition:',
          error
        );
        return { success: false, error: error.message };
      }
    },
    [initializeAnonymousSession, isLoggedIn]
  );

  /**
   * Sync cart from Firebase to Redux (used when cart drawer opens)
   */
  const syncCartFromFirebase = useCallback(async () => {
    try {
      if (!authAvailable || !auth?.currentUser?.uid) {
        return { success: false, error: 'No authenticated user' };
      }

      // Check if we should skip cart sync (e.g., immediately after login)
      // This prevents overwriting the anonymous cart that was just migrated
      const skipCartSyncTimestamp = window.localStorage.getItem('skipCartSync');
      if (skipCartSyncTimestamp) {
        const timeSinceLogin = Date.now() - parseInt(skipCartSyncTimestamp, 10);
        // Skip sync for 3 seconds after login to allow cart migration to settle
        if (timeSinceLogin < 3000) {
          console.log(
            '🛒 [Cart] Skipping cart sync - recently logged in, using Redux cart'
          );
          return {
            success: true,
            skipped: true,
            reason: 'Recently authenticated',
          };
        } else {
          // Clear the flag after 3 seconds
          window.localStorage.removeItem('skipCartSync');
          console.log('🛒 [Cart] Cleared skipCartSync flag');
        }
      }

      const userDoc = await firestore
        .collection('users')
        .doc(auth.currentUser.uid)
        .get();

      if (userDoc.exists) {
        const userData = userDoc.data();
        const bagItems = userData.bagItems || [];

        

        // Ensure userData has uid field
        const completeUserData = {
          ...userData,
          uid: auth.currentUser.uid,
        };

        // Update Redux state
        dispatch(setCart(bagItems));
        dispatch(setUser({ userData: completeUserData }));

        return { success: true, itemCount: bagItems.length };
      } else {
        return { success: false, error: 'User document not found' };
      }
    } catch (error) {
      console.error('❌ [ANONYMOUS] Failed to sync cart from Firebase:', error);
      return { success: false, error: error.message };
    }
  }, [dispatch, authAvailable]);

  /**
   * Add address for anonymous user
   */
  const addAddressForAnonymousUser = useCallback(
    async (address) => {
      try {
        if (!isAnonymous || !token) {
          throw new Error('User must be anonymous to use this function');
        }

        const result = await addAnonymousUserAddress(token, address);
        if (!result.success) {
          throw new Error(result.error);
        }

        return { success: true };
      } catch (error) {
        console.error('❌ [ANONYMOUS] Failed to add address:', error);
        return { success: false, error: error.message };
      }
    },
    [isAnonymous, token]
  );

  /**
   * Handle checkout authentication requirement
   * Only blocks completely unauthenticated users, allows anonymous users to proceed
   */
  const handleCheckoutAuth = useCallback(() => {
    const status = getUserAnonymousStatus();

    // Only block if user is completely not logged in (no auth at all)
    if (!status.isLoggedIn && !status.isAnonymous) {
      // Store current location for redirect after login
      const currentPath = window.location.pathname;
      sessionStorage.setItem('redirectAfterAuth', currentPath);

      // Navigate to signup/login page
      navigate('/signup', {
        state: {
          returnTo: currentPath,
          isUpgrade: false,
          hasCartItems: cart.length > 0,
        },
      });
      return false;
    }

    // Allow anonymous users to see checkout page - they'll be prompted at final order placement
    if (status.isAnonymous) {
      return true;
    }

    // Allow authenticated users
    if (status.isLoggedIn) {
      return true;
    }

    return true;
  }, [navigate, cart.length]);

  /**
   * Handle final order placement authentication requirement
   * Requires full authentication (blocks anonymous users at order placement)
   */
  const handleOrderPlacementAuth = useCallback(() => {
    const status = getUserAnonymousStatus();

    if (!status.isLoggedIn) {
      return false;
    }

    if (status.isAnonymous) {
      return false;
    }

    return true;
  }, []);

  /**
   * Migrate anonymous user to authenticated user
   */
  const migrateToAuthenticatedUser = useCallback(
    async (authenticatedUserId, isNewUser = false) => {
      try {
        if (!isAnonymous || !token) {
          return { success: true, noMigrationNeeded: true };
        }

        const migrationResult = await migrateAnonymousUserData(
          token,
          authenticatedUserId,
          isNewUser
        );

        if (!migrationResult.success) {
          throw new Error(migrationResult.error);
        }

        // Update Redux state
        dispatch(setAnonymousStatus(false));
        dispatch(
          loggedInSuccess({
            token: authenticatedUserId,
            isAnonymous: false,
          })
        );

        // Update cart in Redux with the final cart state
        if (migrationResult.migratedData?.bagItems) {
          dispatch(setCart(migrationResult.migratedData.bagItems));
        }

        return { success: true, migratedData: migrationResult.migratedData };
      } catch (error) {
        console.error('❌ [ANONYMOUS] Migration failed:', {
          error: error.message,
          stack: error.stack,
          authenticatedUserId,
          isNewUser,
        });
        return { success: false, error: error.message };
      }
    },
    [dispatch, isAnonymous, token, cart.length]
  );

  /**
   * Get current anonymous status
   */
  const getAnonymousStatus = useCallback(() => {
    return getUserAnonymousStatus();
  }, []);

  /**
   * Check if user should be prompted for authentication
   */
  const shouldPromptForAuth = useCallback(() => {
    return isAnonymous && cart.length > 0;
  }, [isAnonymous, cart.length]);

  /**
   * Debug function to get current auth state
   */
  const getDebugAuthState = useCallback(() => {
    const firebaseUser = auth?.currentUser;
    return {
      // Redux state
      redux: {
        isLoggedIn,
        isAnonymous,
        token,
        hasUserData: !!userData,
        userDataUid: userData?.uid,
      },
      // Firebase state
      firebase: {
        hasCurrentUser: !!firebaseUser,
        uid: firebaseUser?.uid,
        isAnonymous: firebaseUser?.isAnonymous,
      },
      // Consistency checks
      consistency: {
        uidsMatch: token === firebaseUser?.uid,
        anonymousStatusMatch: isAnonymous === firebaseUser?.isAnonymous,
        userDataHasUid: !!userData?.uid,
      },
      // Cart state
      cart: {
        itemCount: cart.length,
      },
    };
  }, [isLoggedIn, isAnonymous, token, userData, cart.length]);

  // Monitor auth state changes
  useEffect(() => {
    // Check if auth is available before setting up listener
    if (!auth) {
      console.warn(
        '⚠️ [ANONYMOUS] Auth not available, skipping state listener'
      );
      return;
    }

    try {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        if (user) {
          const anonymousStatus = user.isAnonymous;

          // Update anonymous status in Redux if it differs
          if (isAnonymous !== anonymousStatus) {
            dispatch(setAnonymousStatus(anonymousStatus));
          }
        } else {
          dispatch(setAnonymousStatus(false));
        }
      });

      return unsubscribe;
    } catch (error) {
      console.error(
        '❌ [ANONYMOUS] Error setting up auth state listener:',
        error
      );
    }
  }, [dispatch, isAnonymous]);

  return {
    // State - provide safe defaults when auth isn't available
    isAnonymous: authAvailable ? isAnonymous : false,
    isLoggedIn: authAvailable ? isLoggedIn : false,
    userData: authAvailable ? userData : null,
    authAvailable,

    // Actions
    initializeAnonymousSession,
    addToCartWithAnonymousCheck,
    addAddressForAnonymousUser,
    handleCheckoutAuth,
    handleOrderPlacementAuth,
    migrateToAuthenticatedUser,
    getAnonymousStatus,
    shouldPromptForAuth,
    syncCartFromFirebase,

    // Debug utilities
    getDebugAuthState,
  };
};
